import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../ui-kit/input';
import { supabaseClient, updateUserPassword } from '../services/supabase';
import { isValidPassword, PASSWORD_REQUIREMENTS_MESSAGE } from '../shared/passwordValidation';
import { clearPasswordRecoveryPending } from '../utils/passwordRecoveryPending';
import {
    establishRecoverySessionFromUrl,
    getAuthCallbackError,
    stripAuthParamsFromUrl,
} from '../utils/recoverySession';

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [sessionReady, setSessionReady] = useState(false);
    const [sessionError, setSessionError] = useState(() => getAuthCallbackError() ?? '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(true);

    useEffect(() => {
        if (sessionError) {
            stripAuthParamsFromUrl();
            clearPasswordRecoveryPending();
            return;
        }

        let cancelled = false;
        void (async () => {
            const result = await establishRecoverySessionFromUrl();
            if (cancelled) return;
            if (result.ok) {
                setSessionReady(true);
            } else {
                clearPasswordRecoveryPending();
                setSessionError(result.error ?? 'Unable to verify reset link.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionError]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!isValidPassword(password)) {
            setError(PASSWORD_REQUIREMENTS_MESSAGE);
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            await updateUserPassword(password);
            clearPasswordRecoveryPending();
            await supabaseClient.auth.signOut();
            navigate('/login', {
                state: { message: 'Your password has been updated. Sign in with your new password.' },
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update password.';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ResetPasswordLayout>
            {sessionError ? (
                <>
                    <h2 className="text-2xl font-semibold text-foreground">Reset link expired</h2>
                    <p className="mt-2 text-sm text-destructive">{sessionError}</p>
                    <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:text-primary/80">
                        Back to sign in
                    </Link>
                </>
            ) : !sessionReady ? (
                <p className="text-muted-foreground">Verifying your reset link…</p>
            ) : (
                <>
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-foreground">Set a new password</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Choose a strong password for your account
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">New password</label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setIsPasswordValid(isValidPassword(e.target.value));
                                }}
                                required
                                autoComplete="new-password"
                                className={!isPasswordValid && password ? 'border-destructive' : ''}
                            />
                            {password && !isPasswordValid && (
                                <p className="text-sm text-muted-foreground">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Confirm new password</label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                            />
                        </div>
                        {error && <p className="text-destructive text-sm">{error}</p>}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-2 px-4 rounded-md text-primary-foreground transition-colors ${
                                isLoading ? 'bg-muted cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                            }`}
                        >
                            {isLoading ? 'Updating…' : 'Update password'}
                        </button>
                    </form>
                    <Link
                        to="/signout"
                        className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground"
                    >
                        Cancel and sign out
                    </Link>
                </>
            )}
        </ResetPasswordLayout>
    );
};

function ResetPasswordLayout({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-full min-h-0 items-center justify-center p-8">
            <div className="w-full max-w-md space-y-8 rounded-2xl bg-card p-8 shadow-lg">{children}</div>
        </div>
    );
}

export default ResetPasswordPage;
